/* ===========================================================
   Guia (/lucro-real-ecommerce) — post-pilar em destaque no blog. Hub que
   linka todos os conceitos, artigos e calculadoras. Categoria "Guia".
   =========================================================== */
import { Link } from "react-router-dom";
import { useSeo } from "../../seo/use-seo";
import { titulo } from "../../seo/site";
import { JsonLd, breadcrumbLd, articleLd } from "../../seo/jsonld";
import { useContentTracking } from "../../analytics/content-tracking";
import { ConteudoShell, PostHeader, CtaDemo } from "./shell";

const HOME = { nome: "Início", path: "/" };
const BLOG = { nome: "Blog", path: "/blog" };
const PATH = "/lucro-real-ecommerce";
const DATA = "2026-06-26";
const TITULO = "Lucro real no e-commerce: o guia completo";
const DEK =
  "Faturamento não é lucro, e ROAS muito menos. Este guia conecta os conceitos que decidem se a sua loja ganha ou perde dinheiro com tráfego pago — do custo do produto à decisão de escalar ou pausar uma campanha.";

export function Pilar() {
  useSeo({
    title: titulo("Lucro real no e-commerce: o guia completo (ROAS, margem e custos)"),
    description:
      "O guia completo para sair do faturamento e enxergar o lucro real da sua loja: ROAS de equilíbrio, margem de contribuição, CMV e como saber se cada campanha dá lucro.",
    path: PATH,
    ogType: "article",
  });
  useContentTracking({ tipo: "guia", titulo: TITULO });

  return (
    <ConteudoShell breadcrumb={[HOME, BLOG, { nome: TITULO, path: PATH }]}>
      <JsonLd data={breadcrumbLd([HOME, BLOG, { nome: TITULO, path: PATH }])} />
      <JsonLd
        data={articleLd({
          titulo: TITULO,
          descricao: DEK,
          path: PATH,
          publicadoEm: DATA,
          atualizadoEm: DATA,
        })}
      />
      <PostHeader categoria="Guia" titulo={TITULO} dek={DEK} publicadoEm={DATA} tempoMin={6} />

      <div className="article-body">
        <h2>1. Por que o ROAS engana</h2>
        <p>
          O <Link to="/glossario/roas">ROAS</Link> mede receita atribuída ÷ gasto com mídia. Ele não
          desconta nenhum custo do produto, então uma campanha pode ter ROAS alto e ainda assim dar
          prejuízo. O caso clássico está no artigo{" "}
          <Link to="/blog/roas-bom-mas-no-prejuizo">ROAS bom, mas no prejuízo</Link>.
        </p>

        <h2>2. A base de tudo: margem de contribuição</h2>
        <p>
          A <Link to="/glossario/margem-de-contribuicao">margem de contribuição</Link> é o que cada
          venda deixa depois dos custos variáveis — o <Link to="/glossario/cmv">CMV</Link>, o frete
          e o imposto. É ela que define quanto a sua loja aguenta gastar em mídia. Calcule a sua na{" "}
          <Link to="/calculadora/margem-de-contribuicao">
            calculadora de margem de contribuição
          </Link>
          .
        </p>

        <h2>3. A régua de decisão: ROAS de equilíbrio</h2>
        <p>
          Da margem de contribuição sai o{" "}
          <Link to="/glossario/roas-de-equilibrio">ROAS de equilíbrio</Link>: o mínimo para a
          campanha parar de consumir caixa. Acima dele, escale; abaixo, pause. Descubra o seu na{" "}
          <Link to="/calculadora/roas-de-equilibrio">calculadora de ROAS de equilíbrio</Link>.
        </p>

        <h2>4. O número que paga o boleto: lucro das vendas</h2>
        <p>
          No fim da conta está o <Link to="/glossario/lucro-das-vendas">lucro das vendas</Link>:
          receita menos custos variáveis menos mídia. É o que sobra de fato. Para montar a conta
          passo a passo, veja{" "}
          <Link to="/blog/como-calcular-lucro-de-loja-virtual">
            como calcular o lucro real de uma loja virtual
          </Link>
          .
        </p>

        <h2>5. Como fazer isso sem planilha</h2>
        <p>
          Dá para fazer tudo numa planilha — e perder os domingos nela. O Lucraa+ automatiza a conta
          para lojas Nuvemshop: conecta vendas e anúncios pelas APIs oficiais, desconta CMV, frete e
          imposto e mostra o lucro real por campanha e por produto, com uma recomendação por dia.
        </p>
      </div>

      <CtaDemo />
    </ConteudoShell>
  );
}
